import { useEffect, useState } from "react";

import useLeadsOnCollectionDay from "../../data/useLeadsOnCollectionDays";
import useTrips from "../../data/useTrips";
import useTrucks from "../../data/useTrucks";
import { ILead, LeadStatus } from "../../models/Lead";
import { ITruck } from "../../models/Truck";
import { ICalendarTrip } from "../../models/Trip";
import { PartsStatusValues } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import { getLeadPostcode, getLeadTown } from "../../utils/getLeadLocation";
import { DateTime } from "luxon";
import LoadingWheel from "../shared/LoadingWheel";

const TABLE_HEADERS = (
  <tr>
    <th>Reg</th>
    <th>Make/Model</th>
    <th>Location</th>
    <th>Postcode</th>
    <th>Driver Notes</th>
    <th>Vehicle Notes</th>
    <th>Engine Code</th>
    <th>Parts/Scrap</th>
  </tr>
);

export default function PaginatedJobSheetTable({
  selectedDay,
  week,
  year,
  orderedTrucks: orderedTrucksProp,
}: {
  selectedDay: string | undefined;
  week: number;
  year: number;
  orderedTrucks?: ITruck[];
}) {
  const { leadsOnCollectionDay } = useLeadsOnCollectionDay(selectedDay, "PaginatedJobSheetTable");
  const { trucks } = useTrucks();
  const { trips } = useTrips(year, week);
  const [todaysLeads, setTodaysLeads] = useState<ILead[]>();
  const [todaysTrips, setTodaysTrips] = useState<ICalendarTrip[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (
      selectedDay &&
      !leadsOnCollectionDay.isLoading &&
      leadsOnCollectionDay.data &&
      !trips.isLoading &&
      trips.data &&
      !trucks.isLoading &&
      trucks.data
    ) {
      const selectedDate = DateTime.fromISO(selectedDay).toISODate();

      setTodaysLeads(
        leadsOnCollectionDay.data?.data.leads!.filter(
          (lead) => lead.status !== LeadStatus.Cancelled
        )
      );

      setTodaysTrips(
        trips.data.data.find((trip) => DateTime.fromISO(trip.date).toISODate() === selectedDate)?.trips
      );

      setIsLoading(false);
    }
  }, [
    leadsOnCollectionDay.isLoading,
    leadsOnCollectionDay.data,
    trips.isLoading,
    trips.data,
    trucks.isLoading,
    trucks.data,
    selectedDay,
  ]);

  const handleBackground = (status: PartsStatusValues) => {
    switch (status) {
      case PartsStatusValues["Awaiting Schedule"]:
        return "awaiting";
      case PartsStatusValues["Export - Engine + Frontend"]:
      case PartsStatusValues["Export - Engine"]:
      case PartsStatusValues["Export - Frontend"]:
        return "export";
      case PartsStatusValues["Parts - No Engine"]:
        return "sales";
      case PartsStatusValues["Parts + Export"]:
      case PartsStatusValues["Parts - Engine + Gearbox"]:
        return "sales";
      case PartsStatusValues["Scrap"]:
        return "scrap";
      case PartsStatusValues["Salvage"]:
        return "salvage";
      case PartsStatusValues["Cancelled"]:
        return "cancelled";
      default:
        return "";
    }
  };

  if (isLoading) {
    return <LoadingWheel />;
  }

  const effectiveTrucks = orderedTrucksProp ?? trucks.data?.data ?? [];
  const trucksWithTrips = effectiveTrucks.filter((truck) =>
    todaysTrips?.some((trip) => trip.truckId === truck.id)
  );

  return (
    <>
      {trucksWithTrips.map((truck, i) => (
        <div
          key={truck.id}
          style={i < trucksWithTrips.length - 1 ? { pageBreakAfter: "always", breakAfter: "page" } : undefined}
        >
          <table className="print-table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th
                  colSpan={8}
                  className="truck"
                  style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold" }}
                >
                  {truck.name}
                </th>
              </tr>
              {TABLE_HEADERS}
            </thead>
            <tbody>
              {todaysTrips?.map((trip) => {
                if (trip.truckId !== truck.id) return null;
                const leads = todaysLeads?.filter((lead) =>
                  trip.leads.find((leadId) => leadId === lead.id)
                );
                return leads
                  ?.sort((a: ILead, b: ILead) => (a.order > b.order ? 1 : -1))
                  .map((lead) => (
                    <tr
                      key={lead.id}
                      className={classNames(handleBackground(lead.vehicle?.engineExportStatus!), "")}
                      style={{ fontSize: "14px" }}
                    >
                      <td>{lead.vehicle?.vrm}</td>
                      <td style={{ fontSize: "12px" }}>
                        {lead.vehicle?.combinedMake} {lead.vehicle?.combinedModel}
                      </td>
                      <td style={{ fontSize: "12px" }}>{getLeadTown(lead)}</td>
                      <td>{getLeadPostcode(lead)}</td>
                      <td style={{ whiteSpace: "pre-line" }}>{lead.driverNotes}</td>
                      <td style={{ whiteSpace: "pre-line" }}>{lead.vehicle?.notes}</td>
                      <td style={{ fontSize: "12px" }}>{lead.vehicle?.engineModelCode}</td>
                      <td style={{ fontSize: "12px" }}>
                        {PartsStatusValues[lead.vehicle?.engineExportStatus!].toString()}
                      </td>
                    </tr>
                  ));
              })}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}
